import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';

// See ForgotPassword.jsx — backend mounts auth under /api. Earlier builds
// dropped the prefix and the request silently hit the React static server.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reset Password Page
 * User sets a new password using the reset token from email.
 *
 * UX guarantees:
 *  - Submit button shows loading state and is disabled while in flight.
 *  - HTML/non-JSON responses are detected so we never claim success when
 *    the request missed the API.
 *  - Backend 400/404 errors are surfaced as a clear toast in user's language.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || success) return; // duplicate-click guard

    if (!token) {
      toast.error(t('resetPassword.errInvalidLink'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('resetPassword.errMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('resetPassword.errTooShort'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: newPassword },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
          validateStatus: (s) => s >= 200 && s < 500,
        }
      );

      const data = response.data;
      const okShape = data && typeof data === 'object' && data.success === true;

      if (response.status >= 400 || !okShape) {
        // Backend returned 400/404 with a JSON detail OR (worst case) HTML
        const detail =
          data && typeof data === 'object'
            ? data.detail || data.message
            : null;
        // Map common backend errors to translated, user-friendly strings
        const detailStr = typeof detail === 'string' ? detail.toLowerCase() : '';
        let msg;
        if (detailStr.includes('expired') || detailStr.includes('invalid')) {
          msg = t('resetPassword.errExpiredToken');
        } else if (detailStr.includes('password must') || detailStr.includes('characters')) {
          msg = t('resetPassword.errTooShort');
        } else if (typeof detail === 'string') {
          msg = detail; // pass-through for any other backend-specific reason
        } else {
          msg = t('resetPassword.errGeneric');
        }
        throw new Error(msg);
      }

      setSuccess(true);
      toast.success(t('resetPassword.successToast'));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Reset password error:', error);
      }
      const isNetwork =
        error?.code === 'ECONNABORTED' ||
        error?.message === 'Network Error' ||
        !error?.response && !error?.message;
      toast.error(
        isNetwork
          ? t('resetPassword.errNetwork')
          : (error?.message || t('resetPassword.errGeneric'))
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4"
        data-testid="reset-password-success-view"
      >
        <Card className="w-full max-w-md border-2 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('resetPassword.successTitle')}
                </h2>
                <p className="text-gray-600">{t('resetPassword.successBody')}</p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 min-h-[44px]"
                data-testid="reset-password-go-to-login-btn"
              >
                {t('resetPassword.goToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4"
        data-testid="reset-password-invalid-view"
      >
        <Card className="w-full max-w-md border-2 border-red-200">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {t('resetPassword.invalidLinkTitle')}
              </h2>
              <p className="text-gray-600">{t('resetPassword.invalidLinkBody')}</p>
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 min-h-[44px]"
                data-testid="reset-password-request-new-btn"
              >
                {t('resetPassword.requestNewLink')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4"
      data-testid="reset-password-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t('resetPassword.title')}</CardTitle>
          <p className="text-sm text-gray-600">{t('resetPassword.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
            data-testid="reset-password-form"
          >
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('resetPassword.newPasswordLabel')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10 min-h-[44px]"
                  autoFocus
                  autoComplete="new-password"
                  data-testid="reset-password-new-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 min-h-[24px] min-w-[24px]"
                  aria-label={showPassword ? t('resetPassword.hidePassword') : t('resetPassword.showPassword')}
                  data-testid="reset-password-toggle-new-visibility"
                >
                  {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('resetPassword.requirementsHint')}</p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('resetPassword.confirmPasswordLabel')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 min-h-[44px]"
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 min-h-[24px] min-w-[24px]"
                  aria-label={showConfirm ? t('resetPassword.hidePassword') : t('resetPassword.showPassword')}
                  data-testid="reset-password-toggle-confirm-visibility"
                >
                  {showConfirm ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 min-h-[44px]"
              data-testid="reset-password-submit-btn"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('resetPassword.resetting')}
                </span>
              ) : (
                t('resetPassword.resetPassword')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
